import React, { useContext, useMemo, useState } from 'react';
import { IOTTableWrapper, Card, DeleteDialog, PrimaryButton, useSnackbar } from '@Iot-Bee/standard-web-library';
import { noCacheFetcher } from '../../utils/fetch';
import DeviceSettingDialog from './components/DeviceSettingDialog';
import { DeviceNameAndUsed, ServerDeviceSetting, UpdateDeviceSetting } from '../../Types/types';
import { DeviceSetting } from '../../Types/types';
import { useCustomSWR } from '../../hooks/customSWR';
import { columns } from './util/util';
import { DevicesContext } from '../../context/DevicesContext';
import { serverDeviceSettingToBrowser } from './util/util';

const DeviceSettings = () => {
  const deviceContext = useContext(DevicesContext);
  if (!deviceContext) {
    throw new Error('DevicesContext not found');
  }

  const { devices } = deviceContext;
  const {
    data: rawDeviceSettings,
    isLoading,
    mutate: refreshDeviceSettings,
  } = useCustomSWR<ServerDeviceSetting[]>(`${process.env.REACT_APP_API_URL}deviceSettings`);

  const displayDeviceSettings = useMemo(() => {
    if (!rawDeviceSettings) return [];

    const mapped = rawDeviceSettings.map((deviceSetting) => {
      return serverDeviceSettingToBrowser(deviceSetting);
    });

    return mapped;
  }, [rawDeviceSettings]);

  const [dialogInfo, setDialogInfo] = useState<{
    open: boolean;
    mode: 'add' | 'edit';
    deviceSettingToEdit: DeviceSetting | null;
  }>({ open: false, mode: 'add', deviceSettingToEdit: null });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; deviceSetting: DeviceSetting | null }>({
    open: false,
    deviceSetting: null,
  });

  const devicesAndUsed: DeviceNameAndUsed[] = useMemo(() => {
    if (!devices || !rawDeviceSettings) return [];

    return [
      { name: 'Select all devices', isUsed: '', selectAll: true },
      ...devices.map((device) => {
        const isUsed = rawDeviceSettings.find((deviceSetting) =>
          deviceSetting.devices.find(
            (dsDevice) => dsDevice.friendlyName === device.deviceName || dsDevice.imei === device.deviceName
          )
        );
        return { name: device.deviceName, isUsed: isUsed?.name || '' };
      }),
    ];
  }, [devices, rawDeviceSettings]);

  const {
    actions: { openSnackbar },
  } = useSnackbar();

  const handleAddSetting = (deviceSetting: UpdateDeviceSetting) => {
    const stringedDevices = deviceSetting.devices.map((device) => device.name);

    const body = {
      name: deviceSetting.name,
      devices: stringedDevices,
      transmissionSettings: deviceSetting.transmissionSettings,
      collectionSettings: deviceSetting.collectionSettings,
    };
    noCacheFetcher(`${process.env.REACT_APP_API_URL}addDeviceSetting`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then((res) => {
        if (res.status === 200) {
          setDialogInfo((prev) => ({ open: false, mode: prev.mode, deviceSettingToEdit: null }));
          refreshDeviceSettings();
          openSnackbar('success', 'Setting added');
        } else {
          openSnackbar('error', 'Error adding setting');
        }
      })
      .catch((e) => {
        console.error('Error adding setting', e);
      });
  };

  const handleDelete = (id: number) => {
    if (!rawDeviceSettings) {
      openSnackbar('error', 'Error deleting setting');
      return;
    }
    const deviceSetting = rawDeviceSettings.find((deviceSetting) => deviceSetting.id === id);
    if (!deviceSetting) {
      return;
    }
    noCacheFetcher(`${process.env.REACT_APP_API_URL}device-settings/${id}`, { method: 'DELETE' })
      .then((res) => {
        if (res.status === 200) {
          refreshDeviceSettings();
          openSnackbar('success', 'Setting deleted');
        } else {
          openSnackbar('error', 'Error deleting setting');
        }
      })
      .catch((e) => {
        console.error('Error deleting device setting', e);
        openSnackbar('error', 'Error deleting setting');
      });
  };

  const handleUpdate = (deviceSetting: UpdateDeviceSetting) => {
    const stringedDevices = deviceSetting.devices.map((device) => device.name);

    const body = {
      name: deviceSetting.name,
      devices: stringedDevices,
      transmissionSettings: deviceSetting.transmissionSettings,
      collectionSettings: deviceSetting.collectionSettings,
    };

    noCacheFetcher(`${process.env.REACT_APP_API_URL}device-settings/${deviceSetting.id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((res) => {
      if (res.status === 200) {
        setDialogInfo((prev) => ({ open: false, mode: prev.mode, deviceSettingToEdit: null }));
        refreshDeviceSettings();
        openSnackbar('success', 'Setting updated');
      } else {
        openSnackbar('error', 'Error updating setting');
      }
    });
  };

  return (
    <Card
      title="Device Settings"
      headerActions={
        <PrimaryButton onClick={() => setDialogInfo({ open: true, mode: 'add', deviceSettingToEdit: null })}>
          Add new Setting
        </PrimaryButton>
      }
    >
      <IOTTableWrapper
        columns={columns}
        data={displayDeviceSettings}
        onEdit={(deviceSetting) => {
          setDialogInfo({ open: true, mode: 'edit', deviceSettingToEdit: deviceSetting });
        }}
        onDelete={(itemToDelete) => {
          setDeleteDialog({ open: true, deviceSetting: itemToDelete });
        }}
        isLoading={isLoading}
      />
      <DeleteDialog
        title={deleteDialog.deviceSetting?.name || ''}
        open={deleteDialog.open}
        handleDelete={() => {
          handleDelete(deleteDialog.deviceSetting?.id || -1);
          setDeleteDialog((prev) => ({ ...prev, open: false }));
        }}
        onClose={() => setDeleteDialog((prev) => ({ ...prev, open: false }))}
        itemName={deleteDialog.deviceSetting?.name || ''}
      />
      <DeviceSettingDialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo((prev) => ({ ...prev, open: false }))}
        onSave={dialogInfo.mode === 'add' ? handleAddSetting : handleUpdate}
        mode={dialogInfo.mode}
        deviceSettingToEdit={dialogInfo.deviceSettingToEdit}
        availableDevices={devicesAndUsed}
      />
    </Card>
  );
};

export default DeviceSettings;
